
import { useCallback } from "react";
import { getGroups } from "@/lib/storage";
import { useAuthCheck } from "./use-auth-check";
import { useProductData } from "./use-product-data";
import { useFormValidation } from "./use-form-validation";
import { useFormSubmit } from "./use-form-submit";
import { EditProductFormResult } from "./types";
import { fetchGroups } from "@/lib/storage/group/api";

export const useEditProductForm = (productId: string | undefined): EditProductFormResult => {
  // Check authentication
  useAuthCheck();
  
  // Load product data
  const {
    product,
    isLoading,
    name,
    setName,
    description,
    setDescription,
    category,
    setCategory,
    currentStock,
    setCurrentStock,
    usageRate,
    setUsageRate,
    minStockAlert,
    setMinStockAlert,
    selectedGroups,
    setSelectedGroups,
    packageSize,
    setPackageSize,
    supplierName,
    setSupplierName,
    supplierWebsite,
    setSupplierWebsite,
    supplierContact,
    setSupplierContact,
    productCode,
    setProductCode
  } = useProductData(productId);
  
  // Form validation
  const { validateForm } = useFormValidation(name, currentStock, usageRate);
  
  // Handle group toggle
  const handleGroupToggle = useCallback((groupId: string) => {
    setSelectedGroups(prev =>
      prev.includes(groupId)
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  }, [setSelectedGroups]);
  
  // Form submission
  const { handleSubmit, isSubmitting, isSubmitDisabled } = useFormSubmit(
    product,
    name,
    description,
    category,
    currentStock,
    usageRate,
    minStockAlert,
    selectedGroups,
    packageSize,
    supplierName,
    supplierWebsite,
    supplierContact,
    productCode,
    validateForm
  );
  
  // Get groups data - fetch from API first, fallback to local storage
  const groups = getGroups();
  
  return {
    product,
    isLoading,
    formState: {
      name,
      description,
      category,
      currentStock,
      usageRate,
      minStockAlert,
      selectedGroups,
      packageSize,
      supplierName,
      supplierWebsite,
      supplierContact,
      productCode,
    },
    setters: {
      setName,
      setDescription,
      setCategory,
      setCurrentStock,
      setUsageRate,
      setMinStockAlert,
      setPackageSize,
      setSupplierName,
      setSupplierWebsite,
      setSupplierContact,
      setProductCode,
    },
    handleGroupToggle,
    handleSubmit,
    groups,
    isSubmitting,
    isSubmitDisabled
  };
};

// Re-export for easier imports
export * from "./types";
